import { Arrays, ByteArrayInput, ByteArrayOutput, Option, UUID } from "@opendaw/lib-std";
import { IndexedBox, PointerField } from "@opendaw/lib-box";
import { AudioUnitBox, CaptureAudioBox, CaptureMidiBox, NoopInstrumentBox } from "@opendaw/studio-boxes";
import { AudioUnitType, Pointers } from "@opendaw/studio-enums";
import { ProjectSkeleton } from "../project/ProjectSkeleton";
import { TransferUtils } from "../transfer";
import { PresetHeader } from "./PresetHeader";
export var PresetEncoder;
(function (PresetEncoder) {
    PresetEncoder.encode = (audioUnitBox, options = {}) => {
        const header = ByteArrayOutput.create();
        header.writeInt(PresetHeader.MAGIC_HEADER_OPEN);
        header.writeInt(PresetHeader.FORMAT_VERSION);
        const preset = ProjectSkeleton.empty({ createOutputMaximizer: false, createDefaultUser: false });
        const { boxGraph, mandatoryBoxes: { rootBox, primaryAudioBusBox } } = preset;
        const audioUnitBoxes = [audioUnitBox];
        const excludeEffect = options.excludeEffect ?? (() => false);
        const includeTimeline = options.includeTimeline === true;
        const excludeBox = (box) => TransferUtils.shouldExclude(box)
            || (!includeTimeline && TransferUtils.excludeTimelinePredicate(box))
            || excludeEffect(box);
        boxGraph.beginTransaction();
        const dependencies = Array.from(audioUnitBox.graph.dependenciesOf(audioUnitBoxes, {
            alwaysFollowMandatory: true,
            stopAtResources: true,
            excludeBox
        }).boxes);
        const summary = {};
        for (const dep of dependencies) {
            summary[dep.name] = (summary[dep.name] ?? 0) + 1;
        }
        console.info(`PresetEncoder.encode: includeTimeline=${includeTimeline}, deps=${dependencies.length}`, summary);
        const uuidMap = TransferUtils.generateMap(audioUnitBoxes, dependencies, rootBox.audioUnits.address.uuid, primaryAudioBusBox.address.uuid);
        TransferUtils.copyBoxes(uuidMap, boxGraph, audioUnitBoxes, dependencies);
        TransferUtils.reorderAudioUnits(uuidMap, audioUnitBoxes, rootBox);
        boxGraph.endTransaction();
        return Arrays.concatArrayBuffers(header.toArrayBuffer(), boxGraph.toArrayBuffer());
    };
    PresetEncoder.encodeEffects = (effects, kind) => {
        const header = ByteArrayOutput.create();
        header.writeInt(PresetHeader.MAGIC_HEADER_OPEN);
        header.writeInt(PresetHeader.FORMAT_VERSION);
        const preset = ProjectSkeleton.empty({ createOutputMaximizer: false, createDefaultUser: false });
        const { boxGraph, mandatoryBoxes: { rootBox, primaryAudioBusBox } } = preset;
        boxGraph.beginTransaction();
        const captureBox = kind === PresetHeader.ChainKind.Audio
            ? CaptureAudioBox.create(boxGraph, UUID.generate())
            : CaptureMidiBox.create(boxGraph, UUID.generate());
        const wrapperAudioUnit = AudioUnitBox.create(boxGraph, UUID.generate(), box => {
            box.collection.refer(rootBox.audioUnits);
            box.output.refer(primaryAudioBusBox.input);
            box.index.setValue(0);
            box.type.setValue(AudioUnitType.Instrument);
            box.capture.refer(captureBox);
        });
        NoopInstrumentBox.create(boxGraph, UUID.generate(), box => {
            box.host.refer(wrapperAudioUnit.input);
        });
        if (effects.length > 0) {
            const sourceGraph = effects[0].graph;
            const excludeBox = (box) => TransferUtils.shouldExclude(box)
                || TransferUtils.excludeTimelinePredicate(box)
                || box instanceof AudioUnitBox;
            const effectSet = new Set(effects);
            const dependencies = Array.from(sourceGraph.dependenciesOf(effects, {
                alwaysFollowMandatory: true,
                stopAtResources: true,
                excludeBox
            }).boxes).filter(box => !effectSet.has(box));
            const uuidMap = UUID.newSet(({ source }) => source);
            uuidMap.addMany([
                ...effects.map(box => ({ source: box.address.uuid, target: UUID.generate() })),
                ...dependencies.map(box => ({
                    source: box.address.uuid,
                    target: box.resource === "preserved" ? box.address.uuid : UUID.generate()
                }))
            ]);
            const targetField = kind === PresetHeader.ChainKind.Audio
                ? wrapperAudioUnit.audioEffects
                : wrapperAudioUnit.midiEffects;
            const hostPointerType = kind === PresetHeader.ChainKind.Audio
                ? Pointers.AudioEffectHost
                : Pointers.MIDIEffectHost;
            PointerField.decodeWith({
                map: (pointer, address) => {
                    // An INTERNAL target (a box copied along with the chain) always re-targets to its copy —
                    // checked FIRST, and including host pointers: an effect nested inside a copied composite
                    // entry is hosted BY THAT ENTRY, and re-targeting it onto the wrapper chain would rip it
                    // out and flatten the composite.
                    const internal = address.flatMap(addr => uuidMap.opt(addr.uuid).map(({ target }) => addr.moveTo(target)));
                    if (internal.nonEmpty()) {
                        return internal;
                    }
                    // A host pointing OUTSIDE the copied set is a chain ROOT: re-target it onto the wrapper.
                    if (pointer.pointerType === hostPointerType) {
                        return Option.wrap(targetField.address);
                    }
                    return address.flatMap(addr => boxGraph.findBox(addr.uuid).nonEmpty() ? Option.wrap(addr) : Option.None);
                }
            }, () => {
                effects.forEach((source, i) => {
                    const input = new ByteArrayInput(source.toArrayBuffer());
                    const uuid = uuidMap.get(source.address.uuid, "uuid mapping").target;
                    boxGraph.createBox(source.name, uuid, box => {
                        box.read(input);
                        if (IndexedBox.isIndexedBox(box)) {
                            box.index.setValue(i);
                        }
                    });
                });
                dependencies.forEach(source => {
                    const input = new ByteArrayInput(source.toArrayBuffer());
                    const uuid = uuidMap.get(source.address.uuid, "uuid mapping").target;
                    boxGraph.createBox(source.name, uuid, box => box.read(input));
                });
            });
        }
        boxGraph.endTransaction();
        return Arrays.concatArrayBuffers(header.toArrayBuffer(), boxGraph.toArrayBuffer());
    };
})(PresetEncoder || (PresetEncoder = {}));
